import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useDropdownPlacement from 'application/hooks/useDropdownPlacement';
import { usePlayerContext } from 'application/providers/PlayerProvider';

import {
  DATA_TEST_IDS,
  PROMOS_DROPDOWN_MAX_HEIGHT,
  PROMOS_DROPDOWN_MIN_HEIGHT,
  PROMOS_DROPDOWN_PREFERRED_HEIGHT,
  PROMOS_DROPDOWN_VIEWPORT_GAP,
} from '../consts';
import { getPromoMockPage, PROMO_MOCK_TOTAL_COUNT, type PromoMockTrack } from './fixtures';

const DROPDOWN_GAP = 4;

const getPromoDropdownTopBoundary = (fieldElement: HTMLElement): number | undefined => {
  const modalElement = fieldElement.closest(`[data-testid="${DATA_TEST_IDS.AUDIO_MODAL}"]`);
  const closeButton = modalElement?.querySelector<HTMLElement>('[data-testid="button_modal_screen_close"]');

  return closeButton ? closeButton.getBoundingClientRect().bottom + PROMOS_DROPDOWN_VIEWPORT_GAP : undefined;
};

export const usePromoFieldShellViewModel = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(true);
  const nextPageOffsetRef = useRef(0);
  const { play } = usePlayerContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [items, setItems] = useState<PromoMockTrack[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [selectedPromoIds, setSelectedPromoIds] = useState<string[]>([]);

  const dropdownState = useDropdownPlacement({
    containerRef,
    getTopBoundary: getPromoDropdownTopBoundary,
    isOpen: isDropdownOpen,
    gap: DROPDOWN_GAP,
    maxHeight: PROMOS_DROPDOWN_MAX_HEIGHT,
    minHeight: PROMOS_DROPDOWN_MIN_HEIGHT,
    preferredHeight: PROMOS_DROPDOWN_PREFERRED_HEIGHT,
    viewportGap: PROMOS_DROPDOWN_VIEWPORT_GAP,
  });

  const onLoadMore = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreItems) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoadingMore(true);

    try {
      const nextPage = await getPromoMockPage(nextPageOffsetRef.current);

      if (!isMountedRef.current) {
        return;
      }

      nextPageOffsetRef.current += nextPage.length;
      setItems((currentItems) => [...currentItems, ...nextPage]);
      setHasMoreItems(nextPageOffsetRef.current < PROMO_MOCK_TOTAL_COUNT);
    } finally {
      isLoadingRef.current = false;

      if (isMountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [hasMoreItems]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isDropdownOpen && items.length === 0) {
      onLoadMore();
    }
  }, [isDropdownOpen, items.length, onLoadMore]);

  useEffect(() => {
    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (target instanceof Element && !containerRef.current?.contains(target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => document.removeEventListener('pointerdown', handleDocumentPointerDown);
  }, []);

  const selectedPromoIdSet = useMemo(() => new Set(selectedPromoIds), [selectedPromoIds]);
  const dropdownId = DATA_TEST_IDS.AUDIO_REPORT_PROMOS_DROPDOWN;

  const onDropdownToggle = useCallback(() => {
    setIsDropdownOpen((currentValue) => !currentValue);
  }, []);

  const onPromoSelectionChange = useCallback((promoId: string, isSelected: boolean) => {
    setSelectedPromoIds((currentIds) => {
      if (isSelected) {
        return currentIds.includes(promoId) ? currentIds : [...currentIds, promoId];
      }

      return currentIds.filter((currentId) => currentId !== promoId);
    });
  }, []);

  const onPromoPlaybackToggle = useCallback(
    (promo: PromoMockTrack) => {
      play({
        fileUuid: promo.id,
        fileName: promo.title,
        fileUrl: promo.audioUrl,
      });
    },
    [play]
  );

  const onReset = useCallback(() => {
    setSelectedPromoIds([]);
  }, []);

  return {
    containerRef,
    dropdownId,
    dropdownState,
    hasMoreItems,
    isDropdownOpen,
    isLoadingMore,
    items,
    selectedCount: selectedPromoIds.length,
    selectedPromoIdSet,
    onDropdownToggle,
    onLoadMore,
    onPromoPlaybackToggle,
    onPromoSelectionChange,
    onReset,
  };
};
